export const DEFAULT_GROUP_HEADER_ID_PREFIX = '__group__:';

export type GroupHeaderRowBase = {
  id: string;
  __isGroupHeader: true;
  groupKey: string;
  groupLabel: string;
  count: number;
  itemIds: string[];
};

export const GROUP_CHECKBOX_STATE = {
  UNCHECKED: 'unchecked',
  INDETERMINATE: 'indeterminate',
  CHECKED: 'checked',
} as const;

export type GroupCheckboxState =
  (typeof GROUP_CHECKBOX_STATE)[keyof typeof GROUP_CHECKBOX_STATE];

export const isGroupHeaderRow = (row: unknown): row is GroupHeaderRowBase => {
  if (row === null || typeof row !== 'object') {
    return false;
  }

  return Reflect.get(row, '__isGroupHeader') === true;
};

export const getGroupHeaderRowId = (
  groupKey: string,
  headerIdPrefix: string = DEFAULT_GROUP_HEADER_ID_PREFIX,
): string => `${headerIdPrefix}${groupKey}`;

export function buildGroupedRows<
  TItem,
  THeader extends GroupHeaderRowBase = GroupHeaderRowBase,
>({
  items,
  getItemId,
  getGroupKey,
  getGroupLabel,
  compareGroupKeys,
  collapsedGroupKeys,
  headerIdPrefix = DEFAULT_GROUP_HEADER_ID_PREFIX,
  decorateHeader,
  knownGroupKeys = [],
}: {
  items: readonly TItem[];
  getItemId: (item: TItem) => string;
  getGroupKey: (item: TItem) => string;
  getGroupLabel: (groupKey: string) => string;
  compareGroupKeys?: (left: string, right: string) => number;
  collapsedGroupKeys: ReadonlySet<string>;
  headerIdPrefix?: string;
  decorateHeader?: (header: GroupHeaderRowBase, groupItems: TItem[]) => THeader;
  knownGroupKeys?: readonly string[];
}): Array<TItem | THeader> {
  const groups = new Map<string, TItem[]>(
    knownGroupKeys.map((groupKey) => [groupKey, []]),
  );

  for (const item of items) {
    const groupKey = getGroupKey(item);
    const existing = groups.get(groupKey);

    if (existing) {
      existing.push(item);
    } else {
      groups.set(groupKey, [item]);
    }
  }

  const orderedKeys = compareGroupKeys
    ? [...groups.keys()].sort(compareGroupKeys)
    : [...groups.keys()];

  return orderedKeys.flatMap((groupKey) => {
    const groupItems = groups.get(groupKey) ?? [];
    const baseHeader: GroupHeaderRowBase = {
      id: getGroupHeaderRowId(groupKey, headerIdPrefix),
      __isGroupHeader: true,
      groupKey,
      groupLabel: getGroupLabel(groupKey),
      count: groupItems.length,
      itemIds: groupItems.map(getItemId),
    };
    const header = decorateHeader
      ? decorateHeader(baseHeader, groupItems)
      : (baseHeader as THeader);

    if (collapsedGroupKeys.has(groupKey)) {
      return [header];
    }

    return [header, ...groupItems];
  });
}

export const resolveGroupedRowsLoadMoreIndex = ({
  rows,
  collapsedGroupKeys,
  hasUnseenGroups,
}: {
  rows: readonly unknown[];
  collapsedGroupKeys: ReadonlySet<string>;
  hasUnseenGroups: boolean;
}): number | null => {
  const [firstPendingGroupEndIndex] = rows.flatMap((row, index) =>
    isGroupHeaderRow(row) &&
    !collapsedGroupKeys.has(row.groupKey) &&
    row.itemIds.length < row.count
      ? [index + row.itemIds.length]
      : [],
  );

  if (firstPendingGroupEndIndex !== undefined) {
    return firstPendingGroupEndIndex;
  }

  return hasUnseenGroups ? rows.length - 1 : null;
};

export const computeGroupCheckboxState = ({
  itemIds,
  isItemSelected,
  expectedTotalCount,
}: {
  itemIds: readonly string[];
  isItemSelected: (id: string) => boolean;
  expectedTotalCount?: number;
}): GroupCheckboxState => {
  if (itemIds.length === 0) {
    return GROUP_CHECKBOX_STATE.UNCHECKED;
  }

  const selectedCount = itemIds.filter((id) => isItemSelected(id)).length;

  if (selectedCount === 0) {
    return GROUP_CHECKBOX_STATE.UNCHECKED;
  }

  const isFullyLoaded =
    expectedTotalCount === undefined || itemIds.length >= expectedTotalCount;

  if (selectedCount === itemIds.length && isFullyLoaded) {
    return GROUP_CHECKBOX_STATE.CHECKED;
  }

  return GROUP_CHECKBOX_STATE.INDETERMINATE;
};

export const toGroupCheckboxChecked = (
  state: GroupCheckboxState,
): boolean | typeof GROUP_CHECKBOX_STATE.INDETERMINATE =>
  state === GROUP_CHECKBOX_STATE.CHECKED
    ? true
    : state === GROUP_CHECKBOX_STATE.INDETERMINATE
      ? GROUP_CHECKBOX_STATE.INDETERMINATE
      : false;

export const toggleIdsInSet = ({
  ids,
  itemIds,
  shouldInclude,
}: {
  ids: ReadonlySet<string>;
  itemIds: readonly string[];
  shouldInclude: boolean;
}): Set<string> => {
  if (shouldInclude) {
    return new Set([...ids, ...itemIds]);
  }

  const removedIds = new Set(itemIds);

  return new Set([...ids].filter((id) => !removedIds.has(id)));
};
